import { FinanceApprovalItem } from "@/services/financeService";
import { formatCompanyDate } from "@/utils/dateUtils";
import { Card, Modal, Table, Tag } from "antd";

interface ViewApprovalModalProps {
  /**
   * Whether the modal is open
   */
  open: boolean;
  /**
   * The approval item to display
   */
  approval: FinanceApprovalItem | null;
  /**
   * Callback when modal is closed
   */
  onClose: () => void;
  /**
   * Date format from company settings
   */
  dateFormat?: string;
}

/**
 * ViewApprovalModal - Modal to display full approval details including line items
 *
 * Displays invoice request data for pending/approved/rejected approvals.
 * Shows line items table, service period, notes, and submission/approval info.
 */
export default function ViewApprovalModal({ open, approval, onClose, dateFormat }: ViewApprovalModalProps) {
  if (!approval) return null;
  console.log("approval", approval)

  const { invoiceRequest } = approval;

  // Status color mapping
  const statusColorMap: Record<string, string> = {
    Pending: "warning",
    Approved: "success",
    "Auto-Approved": "success",
    Rejected: "error",
  };

  // Priority color mapping
  const priorityColorMap: Record<string, string> = {
    High: "red",
    Medium: "blue",
    Low: "default",
  };

  // Line items table columns
  const lineItemColumns = [
    {
      title: "Product/Service",
      dataIndex: "productService",
      key: "productService",
      render: (value: string) => <span className="font-medium">{value || "—"}</span>,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (value: string) => <span className="text-gray-600">{value || "—"}</span>,
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      align: "center" as const,
      width: 80,
      render: (value: number) => value || 0,
    },
    {
      title: "Rate",
      dataIndex: "rate",
      key: "rate",
      align: "right" as const,
      width: 100,
      render: (value: number) => `$${(value || 0).toLocaleString()}`,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right" as const,
      width: 120,
      render: (value: number) => <span className="font-semibold">${(value || 0).toLocaleString()}</span>,
    },
  ];

  return (
    <Modal open={open} onCancel={onClose} footer={null} width={800} title="Approval Details" destroyOnClose>
      <div className="space-y-6">
        {/* Header Section */}
        <Card className="bg-gray-50">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-500">Invoice Number</p>
              <p className="text-lg font-semibold text-blue-600">{approval.invoiceNumber || "Pending"}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Amount</p>
              <p className="text-lg font-bold text-gray-900">${approval.amount.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Status</p>
              <Tag color={statusColorMap[approval.status] || "default"}>{approval.status}</Tag>
            </div>
            <div>
              <p className="text-sm text-gray-500">Priority</p>
              <Tag color={priorityColorMap[approval.priority] || "default"}>{approval.priority}</Tag>
            </div>
          </div>
        </Card>

        {/* Company & Client Info */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-500">Company</p>
            <p className="font-medium">{approval.companyName}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Client</p>
            <p className="font-medium">{approval.clientName}</p>
            {invoiceRequest?.clientEmail && <p className="text-sm text-gray-500">{invoiceRequest.clientEmail}</p>}
          </div>
        </div>

        {/* Dates & Service Info */}
        {invoiceRequest && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {invoiceRequest.issueDate && (
              <div>
                <p className="text-sm text-gray-500">Issue Date</p>
                <p className="font-medium">{formatCompanyDate(invoiceRequest.issueDate, dateFormat)}</p>
              </div>
            )}
            {invoiceRequest.dueDate && (
              <div>
                <p className="text-sm text-gray-500">Due Date</p>
                <p className="font-medium">{formatCompanyDate(invoiceRequest.dueDate, dateFormat)}</p>
              </div>
            )}
            {invoiceRequest.saleType && (
              <div>
                <p className="text-sm text-gray-500">Sale Type</p>
                <Tag>{invoiceRequest.saleType}</Tag>
              </div>
            )}
            {(invoiceRequest.serviceStartDate || invoiceRequest.serviceEndDate) && (
              <div>
                <p className="text-sm text-gray-500">Service Period</p>
                <p className="font-medium">
                  {invoiceRequest.serviceStartDate && formatCompanyDate(invoiceRequest.serviceStartDate, dateFormat)}
                  {invoiceRequest.serviceStartDate && invoiceRequest.serviceEndDate && " - "}
                  {invoiceRequest.serviceEndDate && formatCompanyDate(invoiceRequest.serviceEndDate, dateFormat)}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Line Items Table */}
        {invoiceRequest?.items && invoiceRequest.items.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Line Items</h3>
            <Table
              dataSource={invoiceRequest.items.map((item, index) => ({
                ...item,
                key: index,
              }))}
              columns={lineItemColumns}
              pagination={false}
              size="small"
              summary={() => (
                <>
                  {typeof approval.taxTotal === "number" && (
                    <Table.Summary.Row className="bg-gray-50">
                      <Table.Summary.Cell index={0} colSpan={4} className="text-right font-semibold text-gray-500">
                        Tax Total:
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} className="text-right font-semibold text-gray-500">
                        ${approval.taxTotal.toLocaleString()}
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                  <Table.Summary.Row className="bg-gray-100">
                    <Table.Summary.Cell index={0} colSpan={4} className="text-right font-bold text-gray-900">
                      Total Amount:
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} className="text-right font-bold text-gray-900 border-t border-gray-200">
                      ${approval.amount.toLocaleString()}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </>
              )}
            />
          </div>
        )}

        {/* Notes */}
        {invoiceRequest?.notes && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Notes</h3>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="text-gray-700 whitespace-pre-wrap">{invoiceRequest.notes}</p>
            </div>
          </div>
        )}

        {/* Vendor Bill Link */}
        {invoiceRequest?.vendorBillLink && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Vendor Bill Link</h3>
            <a
              href={invoiceRequest.vendorBillLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              {invoiceRequest.vendorBillLink}
            </a>
          </div>
        )}

        {/* Submission & Approval Info */}
        <Card className="bg-gray-50">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Submitted By</p>
              <p className="font-medium">{approval.submittedBy}</p>
              <p className="text-sm text-gray-500">{formatCompanyDate(approval.submittedDate, dateFormat)}</p>
            </div>
            {approval.status !== "Pending" && (
              <div>
                <p className="text-sm text-gray-500">
                  {approval.status === "Rejected" ? "Rejected By" : "Approved By"}
                </p>
                <p className="font-medium">{approval.approvedBy || "—"}</p>
                {approval.approvedDate && (
                  <p className="text-sm text-gray-500">{formatCompanyDate(approval.approvedDate, dateFormat)}</p>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* Rejection Reason */}
        {approval.status === "Rejected" && approval.rejectionReason && (
          <div className="bg-red-50 rounded-lg p-4 border border-red-200">
            <h3 className="text-sm font-semibold text-red-700 mb-2">Rejection Reason</h3>
            <p className="text-red-700">{approval.rejectionReason}</p>
          </div>
        )}
      </div>
    </Modal>
  );
}
